// Added by Sejune Oh on 2019-05-27
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import RootStore from '../..'
import MgmtStoreBase from '../../base/MgmtStoreBase'
import Intl from '@/helper/Intl'

import { APSScheduleItem } from '@/helper/define/apsDefine'
import { reqListApsRuleDependencies } from '@/service/apsRule.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface APSRuleBaseElementProps {
  id?: string
  fk_data_storage_id?: string
  name?: string
  description?: string
  aps_engine?: string
  conditions?: string
  aps_engine_param?: string
  study_status_after_completion?: string
  study_status_after_failure_end?: string
  study_status_after_start?: string
  study_tag_after_completion?: string[]
  schedule_items?: APSScheduleItem[]
  running_schedule?: string
  running_schedule_enabled?: boolean
  retry_count?: number
  retry_interval?: number
}

export interface APSRuleElementProps extends APSRuleBaseElementProps {
  conditions_arr?: Array<string>
  storage_name?: string
  engine_specific_arg_arr?: Array<string>
  study_status_id?: string
  study_tag_id?: string
  items?: Array<APSRuleElementProps>
  enable?: boolean
  is_enable?: string
  is_scheduled?: string
}

export interface APSRuleConditionBaseProps {
  operation?: string
  attribute?: string
  tag_display_name?: string
  values?: Array<string | number | undefined>
  level?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class APSRuleStore extends MgmtStoreBase<APSRuleElementProps> {
  totalCount: number = 0
  elements: Array<APSRuleElementProps> = []
  element?: APSRuleElementProps
  root?: RootStore

  constructor(root: RootStore) {
    super({ storeName: 'APSRuleStore', apiUrl: 'mgmt/aps/rules', _root: root })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      element: observable,
      _setTotalCount: action,
      setElements: action,
      setAPSListWithStorageName: action,
    })
  }

  getElement = async (id: string) => {
    return await super.getSingle(id)
  }

  getElements = async (path: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<APSRuleElementProps>
  }

  addElement = async (element: APSRuleElementProps) => {
    const newElement = await super.add(element)
    return newElement.id
  }

  removeElement = async (id: string) => {
    return await super.remove(id)
  }

  updateElement = async (id: string, element: APSRuleElementProps) => {
    const updatedElement = await super.updateSingle(id, element)
    return updatedElement.id
  }

  checkDuplicatedName = (ruleName: string) => {
    return super.checkDuplicatedByField({ field: 'name', value: ruleName })
  }

  getElementsByCompletionStudyStatus = async (fk_data_storage_id: string, study_status_id: string) => {
    const rtnData = await super.postCustom({
      subUrl: ':filterbystudystatus',
      element: {
        fk_data_storage_id,
        study_status_id,
      },
    })

    return rtnData.items as Array<APSRuleElementProps>
  }

  getElementsByStudyTag = async (fk_data_storage_id: string, study_tag_id: string) => {
    const rtnData = await super.postCustom({
      subUrl: ':filterbystudytag',
      element: {
        fk_data_storage_id,
        study_tag_id,
      },
    })

    return rtnData.items as Array<APSRuleElementProps>
  }

  getElementsByConditionStudyStatus = async (fk_data_storage_id: string, study_status_id: string) => {
    const rtnData = await super.postCustom({
      subUrl: ':filterbystudystatus2',
      element: {
        fk_data_storage_id,
        study_status_id,
      },
    })

    return rtnData.items as Array<APSRuleElementProps>
  }

  updateAllApsRuleParam = async () => {
    const rtnData = await super.postCustom({
      subUrl: ':updateall',
    })

    return rtnData.items as Array<APSRuleElementProps>
  }

  getListApsRuleDependencies = async (rule_id: string) => {
    const {
      data: { items },
    } = await reqListApsRuleDependencies(rule_id)
    return items
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<APSRuleElementProps>) => {
    this.elements = elements
  }

  setAPSListWithStorageName = (elements: Array<APSRuleElementProps>, storageNameMap: Map<string, string>) => {
    this.elements = elements.map((element) => ({
      ...element,
      storage_name: storageNameMap.get(element.fk_data_storage_id!),
      is_enable: element.enable!
        ? Intl.getIntlText({ id: 'htmljs_react.global.apsrulestore.text_enabled' })
        : Intl.getIntlText({ id: 'htmljs_react.global.apsrulestore.text_disabled' }),
      is_scheduled: element.running_schedule_enabled!
        ? Intl.getIntlText({ id: 'htmljs_react.global.apsrulestore.text_yes' })
        : Intl.getIntlText({ id: 'htmljs_react.global.apsrulestore.text_no' }),
    }))
  }
}
